'use client';

import { useState } from 'react';
import Image from 'next/image';
import Breadcrumbs from '@/components/ui/Breadcrumbs';
import { getStoreHeroImageSrc } from '@/lib/store-hero-images';
import { Button } from '@/components';

type Props = {
  covaLocationId: number;
  storeName: string;
  city?: string | null;
  neighborhoodLabel?: string | null;
  apiBase?: string;
  endpoint?: string;
  /** The store's own photo. Falls back to bundled art when absent. */
  storeImageUrl?: string | null;
  /** Line-art hero, full card, layer above split base */
  backgroundImageSrc?: string;
  heading?: string;
  bodyCopy?: string;
  breadcrumbRootLabel?: string;
  breadcrumbRootHref?: string;
  submitSource?: string;
  successMessage?: string;
  submitLabel?: string;
  submittingLabel?: string;
};

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:4000';
const DEFAULT_EMAIL_ERROR = 'Please enter a valid email address.';

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export default function LocationSignupHeroSection({
  covaLocationId,
  storeName,
  city,
  neighborhoodLabel,
  storeImageUrl,
  apiBase = API_BASE,
  endpoint = '/api/email-leads',
  backgroundImageSrc = '/media/pages/hero-bg-image.webp',
  heading,
  bodyCopy,
  breadcrumbRootLabel = 'Dispensary',
  breadcrumbRootHref = '/dispensary',
  submitSource,
  successMessage = 'Thanks! We will contact you soon.',
  submitLabel = 'Join now',
  submittingLabel = 'Submitting...',
}: Props) {
  const [form, setForm] = useState({
    firstName: '',
    email: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resolvedHeading = heading || `Dank Cannabis Dispensary ${storeName}`;
  const resolvedBodyCopy =
    bodyCopy ||
    'Visiting us in Ogden for the first time? Join the Dank Club today and receive an exclusive discount on your first in-store purchase. It\'s our way of welcoming you to the neighborhood.';
  const citySegment = city?.trim() || 'Calgary';
  const locationLeaf = neighborhoodLabel?.trim() || storeName;
  const canSubmit = !loading && form.email.trim().length > 0;

  const storeImageSrc = getStoreHeroImageSrc(covaLocationId, storeImageUrl);

  return (
    <section className="relative mt-4 overflow-hidden sm:mt-6" aria-labelledby="location-signup-heading">
      <div className="mx-auto w-full px-4 sm:px-8">
        <div className="relative min-h-[min(520px,78vh)] overflow-hidden rounded-2xl bg-brand-100 sm:min-h-[400px] sm:rounded-2xl lg:min-h-[440px]">
          {/*
            The store's own photograph across the whole card, the way the event
            detail hero does it.

            It used to be a strip pinned to the right with a solid brand panel
            covering the left two thirds and a seam gradient stitching the two
            together, so most of the hero was flat navy and the shop was a
            sliver. One photo, one scrim, and the copy sits on top of it.

            Scrim weights match the event hero: enough at the left and bottom to
            hold the form and breadcrumbs, thin over the rest.
          */}
          <div className="absolute inset-0 z-0">
            <Image
              src={storeImageSrc}
              alt={`${storeName} storefront`}
              fill
              className="object-cover object-center"
              sizes="100vw"
              priority
            />
          </div>

          {/*
           * Same scrim as the event detail hero, so the two heroes read as one
           * treatment: flat 0.45, then weight pushed to the bottom.
           *
           * There was a third layer here, a left-to-right gradient opening at
           * 0.85, which took the composite over the copy to about 0.92 and
           * turned the shop into a flat navy panel. The join form carries its
           * own translucent card, so it does not need the band behind it to be
           * near opaque.
           */}
          <div className="pointer-events-none absolute inset-0 z-[1] bg-brand-100/[0.45]" aria-hidden />
          <div
            className="pointer-events-none absolute inset-0 z-[2] bg-[linear-gradient(180deg,rgba(23,22,90,0.12)_0%,rgba(23,22,90,0.4)_52%,rgba(23,22,90,0.78)_100%)]"
            aria-hidden
          />
          <div className="pointer-events-none absolute inset-0 z-[3]">
            <Image
              src={backgroundImageSrc}
              alt=""
              fill
              className="object-cover object-center opacity-[0.04]"
              sizes="100vw"
              priority
              aria-hidden
            />
          </div>

          <div className="pointer-events-auto relative z-[6] flex min-h-[min(520px,78vh)] w-max max-w-full min-w-0 flex-col justify-center p-4 sm:min-h-[400px] sm:p-8 lg:min-h-[440px] lg:p-10">
            <Breadcrumbs
              tone="onDark"
              rootLabel={breadcrumbRootLabel}
              rootHref={breadcrumbRootHref}
              items={[{ label: citySegment }, { label: locationLeaf }]}
              className="mb-5"
            />

            <div className="w-full max-w-[600px] rounded-2xl border border-constant-white/25 bg-black/25 p-4 backdrop-blur-[3px] sm:p-6">
                <h1
                  id="location-signup-heading"
                  className="text-h3-mobile font-bold leading-tight text-constant-white sm:text-h3-desktop"
                >
                  {resolvedHeading}
                </h1>
                <p className="mt-3 text-body-14-desktop font-normal leading-relaxed text-constant-white/90 sm:text-body-16-desktop">
                  {resolvedBodyCopy}
                </p>

                <form
                  className="mt-5 space-y-3"
                  onSubmit={async (event) => {
                    event.preventDefault();
                    setError(null);
                    setSuccess(false);

                    if (!isValidEmail(form.email)) {
                      setError(DEFAULT_EMAIL_ERROR);
                      return;
                    }

                    setLoading(true);

                    try {
                      const response = await fetch(`${apiBase}${endpoint}`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          email: form.email.trim(),
                          name: form.firstName.trim() || undefined,
                          source: submitSource || `location-page:${storeName}`,
                        }),
                      });

                      const data = await response.json();
                      if (data?.success) {
                        setSuccess(true);
                        setForm({ firstName: '', email: '' });
                        return;
                      }

                      setError(data?.error || 'Failed to submit. Please try again.');
                    } catch (submitError: unknown) {
                      setError(submitError instanceof Error ? submitError.message : 'Failed to submit. Please try again.');
                    } finally {
                      setLoading(false);
                    }
                  }}
                >
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={(event) => setForm((prev) => ({ ...prev, firstName: event.target.value }))}
                    placeholder="Your name"
                    autoComplete="given-name"
                    className="h-12 w-full rounded-xl border border-neutral-10 bg-constant-white px-4 text-body-md text-neutral-80 outline-none transition placeholder:text-neutral-40 focus:border-blue-70"
                  />
                  <input
                    type="email"
                    value={form.email}
                    onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                    placeholder="Your email"
                    required
                    autoComplete="email"
                    className="h-12 w-full rounded-xl border border-neutral-10 bg-constant-white px-4 text-body-md text-neutral-80 outline-none transition placeholder:text-neutral-40 focus:border-blue-70"
                  />
                  <Button
                    type="submit"
                    variant="glass"
                    size="lg"
                    disabled={!canSubmit}
                    className="w-full rounded-xl"
                  >
                    {loading ? submittingLabel : submitLabel}
                  </Button>
                </form>

                {success ? (
                  <p className="mt-3 text-body-12-desktop text-constant-white/90">{successMessage}</p>
                ) : null}
                {error ? <p className="mt-3 text-body-12-desktop text-red-100">{error}</p> : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
